/**
 * BLRRD Pointwise Filters — Color Balance
 *
 * Three-tone-region (Shadows / Midtones / Highlights) × three axes
 * (Cyan↔Red, Magenta↔Green, Yellow↔Blue) = 9 sliders covering
 * everything a colorist needs in < 30 seconds.
 *
 * Algorithm: Adobe Photoshop's documented Color Balance formulas
 * (public since v1.0, 1990 — cyan-red / magenta-green / yellow-blue
 * axes on shadows / midtones / highlights are the canonical UX).
 * Ramp constants `a = 0.25`, `b = 0.333`, `scale = 0.7` are
 * documented in the BLRRD research dossier (docs/research/gimp.md
 * §3, "Color Balance") as constants used by Photoshop-equivalent
 * implementations. NOT cited from GIMP source.
 *
 * Per pixel:
 *   1. Compute luminance L = Rec.709 luma of (R,G,B)
 *   2. Build three weighted ramps over L:
 *        wShadow    = clamp((b - L) / a, 0, 1)
 *        wHighlight = clamp((L - (1 - b)) / a, 0, 1)
 *        wMidtone   = max(0, 1 - wShadow - wHighlight)
 *   3. For each channel C in {R, G, B}:
 *        delta = SCALE * (wShadow * shadowOffset[C]
 *                       + wMidtone * midtoneOffset[C]
 *                       + wHighlight * highlightOffset[C])
 *        C' = C + delta
 *   4. (optional) preserveLuminance: scale back to original Y
 *
 * Each axis offset is a slider in [-100, +100] — converted to
 * [-1, +1] internally and scaled by SCALE_OVERALL = 0.7. Photoshop
 * uses the same magnitude convention. The 0.7 scale prevents
 * full-slider strokes from blowing out — Color Balance is meant
 * to be additive trim, not a primary grade.
 *
 * Pipeline slot: PRE-KERNEL scene-linear ACEScg. Color Balance
 * operates on linear light (offsets are linear, not log) so it
 * sits between ingest and the curve panel.
 *
 * Identity short-circuit: all 9 sliders at 0 → no-op.
 */

/**
 * Ramp half-width on luminance axis. Photoshop / GIMP-equivalent
 * implementations all use 0.25 (research dossier confirmed). Wider
 * ramps mean shadows / midtones / highlights overlap more
 * gradually → smoother transitions but less surgical control.
 */
export const RAMP_A = 0.25;

/**
 * Ramp center on luminance axis. Photoshop / GIMP-equivalent
 * implementations all use 0.333 (and 1 - b = 0.667 for the
 * highlight ramp center). Symmetric around 0.5 so midtones sit
 * exactly at mid-grey.
 */
export const RAMP_B = 0.333;

/**
 * Overall slider scale factor. Slider goes -1..+1 internally;
 * multiplied by SCALE_OVERALL = 0.7 means full-strength slider
 * = 0.7 units of linear-light offset. Matches Photoshop's
 * "feel" — full slider is a strong but recoverable trim.
 */
export const SCALE_OVERALL = 0.7;

const IDENTITY_EPS = 1e-6;

/** Default Color Balance params (no adjustment). */
export function defaultColorBalanceParams() {
    return {
        shadows:    [0, 0, 0],
        midtones:   [0, 0, 0],
        highlights: [0, 0, 0],
        preserveLuminance: false,
    };
}

/** True iff `params` represents a no-op. */
export function isIdentityColorBalance(params) {
    if (!params) return true;
    for (const band of ['shadows', 'midtones', 'highlights']) {
        const arr = params[band];
        if (!arr) continue;
        for (let i = 0; i < 3; i++) {
            if (Math.abs(arr[i] || 0) > IDENTITY_EPS) return false;
        }
    }
    return true;
}

/**
 * Compute the three luminance-band masks for a given L in [0,1].
 * Useful for both the per-pixel loop and the UI ramp visualization.
 *
 * @param {number} L  Luminance in [0, 1] (clamped internally)
 * @returns {[number, number, number]} [wShadow, wMidtone, wHighlight]
 */
export function computeBandWeights(L) {
    const Y = L < 0 ? 0 : (L > 1 ? 1 : L);
    let wS = (RAMP_B - Y) / RAMP_A;
    if (wS < 0) wS = 0; else if (wS > 1) wS = 1;
    let wH = (Y - (1 - RAMP_B)) / RAMP_A;
    if (wH < 0) wH = 0; else if (wH > 1) wH = 1;
    let wM = 1 - wS - wH;
    if (wM < 0) wM = 0;
    return [wS, wM, wH];
}

/**
 * Apply Color Balance to a Float32 RGBA buffer in place.
 *
 * @param {Float32Array} rgba
 * @param {number}       w
 * @param {number}       h
 * @param {object}       params      { shadows, midtones, highlights, preserveLuminance? }
 * @param {object}       [opts]      { selectionMask? }
 * @returns {Float32Array}           same `rgba` reference
 */
export function applyColorBalance(rgba, w, h, params, opts) {
    validateBuffer(rgba, w, h);
    if (isIdentityColorBalance(params)) return rgba;
    const mask = opts && opts.selectionMask ? opts.selectionMask : null;
    if (mask && mask.length !== w * h) {
        throw new Error('applyColorBalance: selectionMask length mismatch');
    }

    /* Slider values come in as [-100, +100]; convert to [-1, +1]. */
    const sR = (params.shadows[0]    || 0) / 100;
    const sG = (params.shadows[1]    || 0) / 100;
    const sB = (params.shadows[2]    || 0) / 100;
    const mR = (params.midtones[0]   || 0) / 100;
    const mG = (params.midtones[1]   || 0) / 100;
    const mB = (params.midtones[2]   || 0) / 100;
    const hR = (params.highlights[0] || 0) / 100;
    const hG = (params.highlights[1] || 0) / 100;
    const hB = (params.highlights[2] || 0) / 100;
    const preserveLum = params.preserveLuminance === true;
    const scale = SCALE_OVERALL;
    const a = RAMP_A;
    const b = RAMP_B;
    const oneMinusB = 1 - b;
    const N = w * h;

    for (let p = 0; p < N; p++) {
        const i = p << 2;
        const mPx = mask ? mask[p] : 1;
        if (mPx <= 0) continue;
        const r = rgba[i], g = rgba[i + 1], B = rgba[i + 2];
        /* Rec.709 luminance — works on AP1-linear too (close enough). */
        let Y = 0.2126 * r + 0.7152 * g + 0.0722 * B;
        const Yclamp = Y < 0 ? 0 : (Y > 1 ? 1 : Y);
        /* Build ramps inline (faster than `computeBandWeights`). */
        let wS = (b - Yclamp) / a;
        if (wS < 0) wS = 0; else if (wS > 1) wS = 1;
        let wH = (Yclamp - oneMinusB) / a;
        if (wH < 0) wH = 0; else if (wH > 1) wH = 1;
        let wM = 1 - wS - wH;
        if (wM < 0) wM = 0;

        const dR = scale * (wS * sR + wM * mR + wH * hR);
        const dG = scale * (wS * sG + wM * mG + wH * hG);
        const dB = scale * (wS * sB + wM * mB + wH * hB);

        let newR = r + dR;
        let newG = g + dG;
        let newB = B + dB;

        if (preserveLum && Y > 1e-6) {
            const newY = 0.2126 * newR + 0.7152 * newG + 0.0722 * newB;
            if (newY > 1e-6) {
                const k = Y / newY;
                newR *= k;
                newG *= k;
                newB *= k;
            }
        }

        if (mPx >= 1) {
            rgba[i]     = newR;
            rgba[i + 1] = newG;
            rgba[i + 2] = newB;
        } else {
            const inv = 1 - mPx;
            rgba[i]     = inv * r + mPx * newR;
            rgba[i + 1] = inv * g + mPx * newG;
            rgba[i + 2] = inv * B + mPx * newB;
        }
    }
    return rgba;
}

function validateBuffer(rgba, w, h) {
    if (!(rgba instanceof Float32Array)) {
        throw new TypeError('applyColorBalance: rgba must be Float32Array');
    }
    if (rgba.length !== w * h * 4) {
        throw new Error('applyColorBalance: rgba.length !== w*h*4');
    }
}
